// client/src/services/analyticsApi.js
// API service for social media analytics
// Fetches KPIs: engagement rate, reach/impressions, CTR, conversion rate, follower growth

/**
 * Fetch analytics overview (high-level KPIs)
 * @param {number} tenantId
 * @param {Object} options - { days, platform }
 */
export async function fetchAnalyticsOverview(tenantId, options = {}) {
  const { days = 30, platform } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/overview/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch overview');
  return data;
}

/**
 * Fetch per-platform analytics breakdown
 * @param {number} tenantId
 * @param {Object} options - { days }
 */
export async function fetchPlatformAnalytics(tenantId, options = {}) {
  const { days = 30 } = options;
  const params = new URLSearchParams({ days: String(days) });

  const res = await fetch(`/api/analytics/platforms/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch platform analytics');
  return data;
}

/**
 * Fetch per-post analytics
 * @param {number} tenantId
 * @param {Object} options - { days, platform, limit, offset, sort }
 */
export async function fetchPostAnalytics(tenantId, options = {}) {
  const { days = 30, platform, limit = 20, offset = 0, sort = 'date' } = options;
  const params = new URLSearchParams({
    days: String(days),
    limit: String(limit),
    offset: String(offset),
    sort,
  });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/posts/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch post analytics');
  return data;
}

/**
 * Fetch follower growth data
 * @param {number} tenantId
 * @param {Object} options - { days, platform }
 */
export async function fetchFollowerGrowth(tenantId, options = {}) {
  const { days = 90, platform } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/followers/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch follower growth');
  return data;
}

/**
 * Fetch engagement/reach trends over time
 * @param {number} tenantId
 * @param {Object} options - { days, platform }
 */
export async function fetchAnalyticsTrends(tenantId, options = {}) {
  const { days = 30, platform } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/trends/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch trends');
  return data;
}

/**
 * Fetch top performing posts
 * @param {number} tenantId
 * @param {Object} options - { days, limit, platform }
 */
export async function fetchTopPosts(tenantId, options = {}) {
  const { days = 30, limit = 10, platform } = options;
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (platform) params.set('platform', platform);

  const res = await fetch(`/api/analytics/top-posts/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch top posts');
  return data;
}

/**
 * Fetch click tracking statistics
 * @param {number} tenantId
 * @param {Object} options - { days, postId }
 */
export async function fetchClickStats(tenantId, options = {}) {
  const { days = 30, postId } = options;
  const params = new URLSearchParams({ days: String(days) });
  if (postId) params.set('postId', String(postId));

  const res = await fetch(`/api/tracking/stats/${tenantId}?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch click stats');
  return data;
}

/**
 * Create a tracking link
 * @param {number} tenantId
 * @param {string} destination - URL to redirect to
 * @param {number} postId - Optional post ID
 */
export async function createTrackingLink(tenantId, destination, postId = null) {
  const res = await fetch('/api/tracking/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ tenantId, destination, postId }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to create tracking link');
  return data;
}

/**
 * Format large numbers for display (e.g., 1234 -> "1.2K")
 */
export function formatMetricNumber(num) {
  if (num === null || num === undefined) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
}

/**
 * Platform display names and colors
 */
export const PLATFORM_META = {
  facebook:  { name: 'Facebook',  color: '#1877F2', icon: 'bi-facebook' },
  instagram: { name: 'Instagram', color: '#E4405F', icon: 'bi-instagram' },
  twitter:   { name: 'X/Twitter', color: '#000000', icon: 'bi-twitter-x' },
  linkedin:  { name: 'LinkedIn',  color: '#0A66C2', icon: 'bi-linkedin' },
  youtube:   { name: 'YouTube',   color: '#FF0000', icon: 'bi-youtube' },
  tiktok:    { name: 'TikTok',    color: '#000000', icon: 'bi-tiktok' },
  pinterest: { name: 'Pinterest', color: '#BD081C', icon: 'bi-pinterest' },
  reddit:    { name: 'Reddit',    color: '#FF4500', icon: 'bi-reddit' },
  bluesky:   { name: 'Bluesky',   color: '#0085FF', icon: 'bi-cloud' },
  twitch:    { name: 'Twitch',    color: '#9146FF', icon: 'bi-twitch' },
  telegram:  { name: 'Telegram',  color: '#0088CC', icon: 'bi-telegram' },
};
